import { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { getApiEndpoint } from '@/lib/api';
import { getToken, getUser } from '@/lib/auth-storage';

interface CompanyBranding {
  logo_url?: string;
  primary_color?: string;
  secondary_color?: string;
  name: string;
}

interface BrandingContextType {
  branding: CompanyBranding | null;
  isLoading: boolean;
}

const BrandingContext = createContext<BrandingContextType>({
  branding: null,
  isLoading: false,
});

export const useBranding = () => useContext(BrandingContext);

interface BrandingProviderProps {
  children: ReactNode;
}

export const BrandingProvider = ({ children }: BrandingProviderProps) => {
  const [branding, setBranding] = useState<CompanyBranding | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    const loadBranding = async () => {
      const userData = getUser();
      const token = getToken();

      console.log('🔍 BrandingContext - Carregando branding...');
      console.log('   User data:', userData ? 'SIM' : 'NÃO');
      console.log('   Token:', token ? 'SIM' : 'NÃO');

      if (userData && token) {
        try {
          const user = JSON.parse(userData);
          console.log('    Usuário:', user.name);
          console.log('    Company ID:', user.company_id);
          
          if (user.company_id) {
            setIsLoading(true);
            console.log(`   📡 Buscando empresa ID ${user.company_id}...`);
            
            const response = await fetch(getApiEndpoint(`/api/companies/${user.company_id}`), {
              headers: {
                Authorization: `Bearer ${token}`,
              },
            });

            console.log('    Response status:', response.status);

            if (response.ok) {
              const data = await response.json();
              console.log('    Dados recebidos:', data);
              
              if (data && data.company) {
                const brandingData = {
                  logo_url: data.company.logo_url,
                  primary_color: data.company.primary_color || '#2563eb',
                  secondary_color: data.company.secondary_color || '#4f46e5',
                  name: data.company.name,
                };
                
                console.log('    Branding data preparado:', brandingData);
                
                setBranding(brandingData);
                
                // Aplicar cores CSS globalmente
                applyBrandingColors(brandingData);
              } else {
                console.log('    Empresa não encontrada nos dados');
              }
            } else {
              console.log('    Erro na resposta da API:', response.status);
            }
          } else {
            // Sem empresa - usar cores padrão
            console.log('   ℹ Usuário sem company_id - usando padrão');
            setBranding(null);
            applyDefaultColors();
          }
        } catch (error) {
          console.error('Erro ao carregar branding:', error);
          setBranding(null);
          applyDefaultColors();
        } finally {
          setIsLoading(false);
        }
      } else {
        // Não logado - resetar TUDO para padrão
        setBranding(null);
        applyDefaultColors();
        console.log(' Branding resetado para padrão (TEC-IT)');
      }
    };

    loadBranding();

    // Listener para mudanças de autenticação
    const handleUserChange = () => {
      loadBranding();
    };

    window.addEventListener('userChanged', handleUserChange);
    window.addEventListener('storage', loadBranding); // Detecta mudanças no localStorage
    
    return () => {
      window.removeEventListener('userChanged', handleUserChange);
      window.removeEventListener('storage', loadBranding);
    };
  }, []);

  return (
    <BrandingContext.Provider value={{ branding, isLoading }}>
      {children}
    </BrandingContext.Provider>
  );
};

// Função para aplicar as cores no CSS
function applyBrandingColors(branding: CompanyBranding) {
  const root = document.documentElement;
  const primary = branding.primary_color || '#2563eb';
  const secondary = branding.secondary_color || '#4f46e5';
  
  // Aplicar cores como CSS variables
  root.style.setProperty('--brand-primary', primary);
  root.style.setProperty('--brand-secondary', secondary);
  root.style.setProperty('--brand-primary-dark', darkenColor(primary, 10));
  root.style.setProperty('--brand-primary-light', lightenColor(primary, 90));
  
  // Atualizar título da página e meta tags
  document.title = `${branding.name} - Sistema de Gestão de Chamados`;
  
  // Atualizar meta description
  const metaDescription = document.querySelector('meta[name="description"]');
  if (metaDescription) {
    metaDescription.setAttribute('content', `Sistema de gerenciamento de chamados de ${branding.name}. Abra, acompanhe e resolva seus tickets com agilidade.`);
  }
  
  // Atualizar og:title
  const ogTitle = document.querySelector('meta[property="og:title"]');
  if (ogTitle) {
    ogTitle.setAttribute('content', `${branding.name} - Sistema de Gestão de Chamados`);
  }
  
  console.log(' BRANDING COMPLETO APLICADO:');
  console.log(`   Empresa: ${branding.name}`);
  console.log(`   Cor Primária: ${primary}`);
  console.log(`   Cor Secundária: ${secondary}`);
  console.log(`   Título: ${branding.name} - Sistema de Gestão de Chamados`);
}

function applyDefaultColors() {
  const root = document.documentElement;
  
  // Resetar TODAS as variáveis de cor para padrão
  root.style.setProperty('--brand-primary', '#2563eb');
  root.style.setProperty('--brand-secondary', '#4f46e5');
  root.style.setProperty('--brand-primary-dark', '#1e40af');
  root.style.setProperty('--brand-primary-light', '#eff6ff');
  
  // Resetar título e meta tags para padrão
  document.title = 'ITSM - Sistema de Gestão de Chamados';
  
  const metaDescription = document.querySelector('meta[name="description"]');
  if (metaDescription) {
    metaDescription.setAttribute('content', 'Sistema completo de gerenciamento de chamados técnicos e operacionais. Abra, acompanhe e resolva seus tickets com agilidade e transparência.');
  }
  
  const ogTitle = document.querySelector('meta[property="og:title"]');
  if (ogTitle) {
    ogTitle.setAttribute('content', 'ITSM - Sistema de Gestão de Chamados');
  }
  
  console.log('🔄 BRANDING RESETADO PARA PADRÃO (TEC-IT)');
}

// Função helper para escurecer cor
function darkenColor(hex: string, percent: number): string {
  const num = parseInt(hex.replace('#', ''), 16);
  const amt = Math.round(2.55 * percent);
  const R = (num >> 16) - amt;
  const G = ((num >> 8) & 0x00ff) - amt;
  const B = (num & 0x0000ff) - amt;
  return '#' + (
    0x1000000 +
    (R < 255 ? (R < 1 ? 0 : R) : 255) * 0x10000 +
    (G < 255 ? (G < 1 ? 0 : G) : 255) * 0x100 +
    (B < 255 ? (B < 1 ? 0 : B) : 255)
  ).toString(16).slice(1);
}

// Função helper para clarear cor
function lightenColor(hex: string, percent: number): string {
  const num = parseInt(hex.replace('#', ''), 16);
  const amt = Math.round(2.55 * percent);
  const R = (num >> 16) + amt;
  const G = ((num >> 8) & 0x00ff) + amt;
  const B = (num & 0x0000ff) + amt;
  return '#' + (
    0x1000000 +
    (R < 255 ? (R < 1 ? 0 : R) : 255) * 0x10000 +
    (G < 255 ? (G < 1 ? 0 : G) : 255) * 0x100 +
    (B < 255 ? (B < 1 ? 0 : B) : 255)
  ).toString(16).slice(1);
}
